<script lang="ts">
  import { CATEGORY_META, categoryLabel, type AnswerRecord, type Category, type Decade } from '@banger/shared';
  import { onMount } from 'svelte';
  import { send, lastError } from '../lib/connection';
  import { decadeLabel, inputKind, MAX_TEXT, toAnswerValue, YEAR_MIN, YEAR_START, yearMax } from '../lib/answerInput';

  let { category, decades, round, answer, cutoff = 2000 }: {
    cutoff?: number | null;
    category: Category;
    decades: Decade[];
    round: number;
    /** The server's record of my answer (authoritative). */
    answer: AnswerRecord | null;
  } = $props();

  // svelte-ignore state_referenced_locally
  const kind = inputKind(category);
  const color = $derived(CATEGORY_META[category].color);
  const max = yearMax();
  const years = Array.from({ length: max - YEAR_MIN + 1 }, (_, i) => max - i);

  let raw = $state(kind === 'year' ? String(YEAR_START) : '');
  const value = $derived(toAnswerValue(category, raw));

  // Optimistic lock-in, reconciled with the server: an error (or a skipped song
  // clearing answers) brings the input back.
  let sentAt = $state(0);
  let now = $state(Date.now());
  $effect(() => {
    if (!sentAt) return;
    const t = setTimeout(() => (now = Date.now()), 1600);
    return () => clearTimeout(t);
  });
  $effect(() => { if ($lastError) sentAt = 0; });
  $effect(() => { if (answer) sentAt = 0; });
  const locked = $derived(answer !== null || (sentAt > 0 && now - sentAt < 1500));

  function lockIn() {
    if (value === null || locked) return;
    send({ type: 'answer', round, value });
    sentAt = Date.now();
    now = sentAt;
    navigator.vibrate?.(20);
  }

  function shownAnswer(v: AnswerRecord['value'] | null): string {
    if (v === null) return '';
    if (typeof v === 'boolean') return v ? 'BEFORE' : 'AFTER';
    if (kind === 'decade' && typeof v === 'number') return `the ${decadeLabel(v)}`;
    return String(v);
  }

  function bump(d: number) {
    const y = Number(raw) || YEAR_START;
    raw = String(Math.min(max, Math.max(YEAR_MIN, y + d)));
  }

  let textEl: HTMLInputElement | undefined = $state();
  onMount(() => textEl?.focus());
</script>

<div class="answer" style="--c:{color}">
  {#if locked}
    <div class="locked" role="status">
      <p class="big">Locked in — listen up!</p>
      <p class="mine">{shownAnswer(answer?.value ?? value)}</p>
    </div>
  {:else}
    {#if kind === 'text'}
      <input
        bind:this={textEl}
        bind:value={raw}
        class="text"
        type="text"
        maxlength={MAX_TEXT}
        placeholder={category === 'title' ? 'Song title…' : "Type the band or singer's name"}
        aria-label={categoryLabel(category, cutoff)}
        autocapitalize="off"
        autocorrect="off"
        autocomplete="off"
        spellcheck="false"
        enterkeyhint="done"
        onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lockIn(); } }}
      />
    {:else if kind === 'decade'}
      <div class="choices decades" role="radiogroup" aria-label="Decade">
        {#each decades as d (d)}
          <button
            type="button" class="choice" role="radio" aria-checked={raw === String(d)}
            class:picked={raw === String(d)} onclick={() => (raw = String(d))}
          >{decadeLabel(d)}</button>
        {/each}
      </div>
    {:else if kind === 'yesno'}
      <div class="choices yesno" role="radiogroup" aria-label={categoryLabel(category, cutoff)}>
        <button type="button" class="choice" role="radio" aria-checked={raw === 'yes'} class:picked={raw === 'yes'} onclick={() => (raw = 'yes')}>BEFORE</button>
        <button type="button" class="choice" role="radio" aria-checked={raw === 'no'} class:picked={raw === 'no'} onclick={() => (raw = 'no')}>AFTER</button>
      </div>
    {:else}
      <div class="dial">
        <button type="button" class="step" aria-label="One year earlier" disabled={Number(raw) <= YEAR_MIN} onclick={() => bump(-1)}>−</button>
        <label class="wheel">
          <span class="year" aria-hidden="true">{raw}</span>
          <select bind:value={raw} aria-label="Year">
            {#each years as y (y)}<option value={String(y)}>{y}</option>{/each}
          </select>
          <span class="hint" aria-hidden="true">tap to scroll</span>
        </label>
        <button type="button" class="step" aria-label="One year later" disabled={Number(raw) >= max} onclick={() => bump(1)}>+</button>
      </div>
    {/if}
    <button type="button" class="btn-neon lock" disabled={value === null} onclick={lockIn}>LOCK IN</button>
  {/if}
</div>

<style>
  .answer { display: grid; gap: 16px; width: 100%; max-width: 420px; margin: 0 auto; }
  button { touch-action: manipulation; }
  .text {
    width: 100%; min-height: 60px; padding: 12px 16px; border-radius: 14px; font: inherit; font-size: 1.3rem; font-weight: 700;
    color: #fff; background: rgba(10, 6, 24, 0.7); border: 2.5px solid var(--c); outline: none;
    box-shadow: 0 0 14px color-mix(in srgb, var(--c) 45%, transparent);
  }
  .text::placeholder { color: rgba(255, 255, 255, 0.4); }
  .choices { display: grid; gap: 10px; }
  .decades { grid-template-columns: repeat(auto-fit, minmax(84px, 1fr)); }
  .yesno { grid-template-columns: 1fr 1fr; }
  .choice {
    min-height: 64px; border-radius: 14px; font: inherit; font-size: 1.5rem; font-weight: 900; cursor: pointer;
    color: var(--c); background: rgba(10, 6, 24, 0.6); border: 2.5px solid var(--c);
  }
  .yesno .choice { min-height: 96px; font-size: 2rem; }
  .choice.picked { background: var(--c); color: #0a0618; box-shadow: 0 0 18px var(--c); }
  .dial { display: grid; grid-template-columns: 64px 1fr 64px; align-items: center; gap: 10px; }
  .step {
    height: 64px; border-radius: 50%; font: inherit; font-size: 2rem; font-weight: 900; cursor: pointer;
    color: var(--c); background: rgba(10, 6, 24, 0.6); border: 2.5px solid var(--c);
  }
  .step:disabled { opacity: 0.3; }
  .wheel {
    position: relative; display: grid; justify-items: center; padding: 8px 0; border-radius: 16px;
    border: 2.5px solid var(--c); background: rgba(10, 6, 24, 0.6); box-shadow: 0 0 18px color-mix(in srgb, var(--c) 45%, transparent);
  }
  .year { font-size: 3.4rem; font-weight: 900; line-height: 1.1; color: #fff; text-shadow: 0 0 12px var(--c); font-variant-numeric: tabular-nums; }
  .hint { font-size: 0.8rem; opacity: 0.6; }
  /* The native select covers the number: tapping it opens the phone's scroll wheel. */
  .wheel select { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; font-size: 16px; cursor: pointer; }
  .lock { min-height: 72px; font-size: 1.6rem; width: 100%; }
  .locked { text-align: center; display: grid; gap: 8px; padding: 24px 12px; border-radius: 16px; border: 2.5px solid var(--c); background: rgba(10, 6, 24, 0.5); }
  .big { margin: 0; font-size: 1.6rem; font-weight: 900; }
  .mine { margin: 0; font-size: 1.3rem; color: var(--c); font-weight: 800; word-break: break-word; }
</style>
